import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type DraftStatus } from '../api/client'
import { listPieces, type PieceRow } from './api'
import { HANDOFF, STAGE_TABS, chipClass, destinationChipClass, destinationLabel, etDate, etTime, stageLabel, type StageKey } from './labels'
import { useCanDraft, useEditorialTitle } from './titleContext'
import { formatLabel, piecesLanded, WRITING_POLL_MS } from './desk'
import { PAGE_SIZE, allCount, parseDestination, parseSent, parseStage, rowAction } from './pieces'
import { pieceHref, startHref } from './links'
import { describeError } from '../lib/describeError'
import { useDebounced } from '../lib/useDebounced'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const field = `min-h-11 rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btnPrimary = `inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const th = 'whitespace-nowrap border-b border-ink-500 px-4 py-3 text-left font-ui text-[12px] font-bold uppercase tracking-[0.06em] text-paper-faint'
const td = 'border-b border-ink-600 px-4 py-3.5 align-middle'

function Row({ row, section, path }: { row: PieceRow; section: string; path: string }) {
  const action = rowAction(row, path)
  return (
    <tr>
      <td className={`${td} max-w-[420px]`}>
        <Link to={pieceHref(path, row.id)} className={`block font-serif text-[18px] font-medium leading-tight text-paper no-underline hover:underline ${focus}`}>{row.title}</Link>
        {row.subject && <span className="block font-ui text-[12px] text-paper-faint">{row.subject}</span>}
        {row.revision_note && <span className="block font-ui text-[12px] text-paper-dim">Revised: “{row.revision_note}”</span>}
        {row.status === 'failed' && row.error && <span className="mt-1 block font-ui text-[12px] text-paper-dim">{row.error}</span>}
        {row.sent_to_editor_at && (
          <span className="mr-1.5 mt-1.5 inline-flex h-6 items-center rounded-full bg-radar-cyan/15 px-2.5 font-ui text-[12px] font-semibold text-radar-cyan">
            {HANDOFF.filter}
          </span>
        )}
        {row.source_changed && (
          <span className="mr-1.5 mt-1.5 inline-flex h-6 items-center rounded-full bg-amber-glow/15 px-2.5 font-ui text-[12px] font-semibold text-amber-deep">
            Source changed
          </span>
        )}
        {row.open_items > 0 && (
          <span className="mt-1.5 inline-flex h-6 items-center rounded-full bg-amber-glow/20 px-2.5 font-ui text-[12px] font-semibold text-amber-deep">
            {row.open_items} to look at
          </span>
        )}
      </td>
      <td className={td}><span className={chipClass(row.status)}>{stageLabel(row.status)}</span></td>
      <td className={td}><span className={destinationChipClass(row.destination)}>{destinationLabel(row.destination, section)}</span></td>
      <td className={`${td} font-ui text-[14px] text-paper-dim`}>{formatLabel(row.format)} · {row.word_count.toLocaleString('en-US')} words</td>
      <td className={`${td} whitespace-nowrap font-ui text-[14px] text-paper-dim`}>
        {row.updated_at ? `${etDate(row.updated_at)}, ${etTime(row.updated_at)}` : '—'}
        {row.requested_by && <span className="block text-[12px] text-paper-faint">{row.requested_by}</span>}
      </td>
      <td className={`${td} text-right`}>
        <Link to={action.href} aria-label={`${action.label}: ${row.title}`} className={`font-ui text-[14px] font-semibold text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>
          {action.label}
        </Link>
      </td>
    </tr>
  )
}

export function PiecesPage() {
  const { section, publicationSlug } = useEditorialTitle()
  const canDraft = useCanDraft()
  const [params, setParams] = useSearchParams()
  const stage = parseStage(params.get('stage'))
  const destination = parseDestination(params.get('destination'))
  const sent = parseSent(params.get('sent'))
  const urlQ = params.get('q') ?? ''
  const [text, setText] = useState(urlQ)
  const debounced = useDebounced(text, 300)

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setParams(next, { replace: true })
  }

  // The debounced text is the one written to the URL.
  useEffect(() => {
    if (debounced !== urlQ) update({ q: debounced.trim() ? debounced : null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  // While the title has a run going, follow it: when a piece lands the list reloads, so an
  // open To review tab shows it without being left and opened again.
  const qc = useQueryClient()
  const run = useQuery({
    queryKey: ['section-draft-status', publicationSlug],
    queryFn: () => api.get<DraftStatus>(`/api/lab/section-drafts/draft-now/status?publication_slug=${publicationSlug}`),
    refetchInterval: (query) => (query.state.data?.running ? WRITING_POLL_MS : 30_000),
  })
  const wasRunning = useRef<DraftStatus | null | undefined>(undefined)
  useEffect(() => {
    if (run.data === undefined) return
    const now = run.data.running ? run.data : null
    if (piecesLanded(wasRunning.current, now)) qc.invalidateQueries({ queryKey: ['editorial-pieces', publicationSlug] })
    wasRunning.current = now
  }, [run.data, publicationSlug, qc])

  const q = useInfiniteQuery({
    queryKey: ['editorial-pieces', publicationSlug, stage, urlQ, destination, sent],
    queryFn: ({ pageParam }) => listPieces(publicationSlug, { stage, q: urlQ, destination, sent, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return last.items.length > 0 && loaded < last.total ? loaded : undefined
    },
  })

  const rows = q.data?.pages.flatMap((p) => p.items) ?? []
  const counts = q.data?.pages[0]?.counts
  const selectedKey = stage ?? 'all'
  const sentCount = q.data?.pages[0]?.sent_to_editor
  const countOf = (key: string) => (key === 'all' ? allCount(counts) : counts?.[key as StageKey])

  return (
    <div className="flex flex-col gap-5 font-ui text-paper">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="label">{section.label} · Pieces</span>
          <h1 className="m-0 font-serif text-[40px] font-medium leading-[1.05] tracking-tight text-paper">Every piece, wherever it is</h1>
        </div>
        {canDraft && <Link to={startHref(section.path)} className={btnPrimary}>Start a piece</Link>}
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Stage" className="flex flex-wrap gap-1 rounded-[10px] bg-ink-800 p-1">
          {STAGE_TABS.map((t) => {
            const on = t.key === selectedKey
            const n = countOf(t.key)
            return (
              <button key={t.key} type="button" role="tab" aria-selected={on}
                onClick={() => update({ stage: t.key === 'to_review' ? null : t.key })}
                className={`min-h-9 rounded-[7px] px-3 font-ui text-[13px] font-semibold ${focus} ${on ? 'bg-ink-600 text-paper' : 'text-paper-dim hover:text-paper'}`}>
                {t.label}{n !== undefined && <span className="ml-1.5 font-medium opacity-70">{n}</span>}
              </button>
            )
          })}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" aria-pressed={sent} onClick={() => update({ sent: sent ? null : '1' })}
                  className={`min-h-11 rounded-lg border px-3 font-ui text-[13px] font-semibold ${focus} ${
                    sent ? 'border-radar-cyan bg-radar-cyan/15 text-radar-cyan' : 'border-ink-400 text-paper-dim hover:text-paper'}`}>
            {HANDOFF.filter}{sentCount !== undefined && <span className="ml-1.5 font-medium opacity-70">{sentCount}</span>}
          </button>
          <label className="flex">
            <span className="sr-only">Search pieces</span>
            <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Search titles and subjects" className={`${field} w-60`} />
          </label>
          <label className="flex">
            <span className="sr-only">Appears in</span>
            <select value={destination ?? ''} onChange={(e) => update({ destination: e.target.value || null })} className={field}>
              <option value="">Anywhere</option>
              <option value="section">{destinationLabel('section', section.label)}</option>
              <option value="membership">{destinationLabel('membership', section.label)}</option>
              <option value="both">{destinationLabel('both', section.label)}</option>
            </select>
          </label>
        </div>
      </div>

      {q.isPending && <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load pieces. {describeError(q.error)}</p>}

      {q.data && (
        <div className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr>
                <th scope="col" className={th}>Piece</th>
                <th scope="col" className={th}>Stage</th>
                <th scope="col" className={th}>Appears in</th>
                <th scope="col" className={th}>Length</th>
                <th scope="col" className={th}>Last change</th>
                <th scope="col" className={th}><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <Row key={r.id} row={r} section={section.label} path={section.path} />)}
              {rows.length === 0 && (
                <tr><td colSpan={6} className={`${td} text-[14px] text-paper-dim`}>No pieces here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {q.hasNextPage && (
        <div className="flex justify-center">
          <button type="button" className={btn} disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {q.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
      <p className="m-0 text-[12px] text-paper-faint">
        Revised pieces show once, at their latest version; earlier versions are in each piece’s History.
      </p>
    </div>
  )
}
